import React, { useState, useRef } from 'react';
import { ArrowUp, Lock as LockIcon } from 'lucide-react';
import PlanGate from '../gating/PlanGate';

/**
 * Common Reusable Empty State Table Component
 * Displays a ghost table with sample/example rows in the background
 * and an elevated card overlay (with clean font-normal typography and zero scroll).
 */
const EmptyStateTable = ({
    title,
    description,
    primaryAction,
    secondaryAction,
    dropZone,
    columns = [],
    rows = [],
    renderRow,
    renderMobileRow,
    showOverlayCard = true,
    children
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);

    const handleDragOver = (e) => {
        e.preventDefault();
        if (dropZone?.isLocked) return;
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (dropZone?.isLocked) {
            if (dropZone.onDropZoneClick) dropZone.onDropZoneClick();
            return;
        }
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0 && dropZone?.onFileDrop) {
            dropZone.onFileDrop(files);
        }
    };

    const handleFileInputChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0 && dropZone?.onFileDrop) {
            dropZone.onFileDrop(files);
        }
    };

    return (
        <div className="relative w-full overflow-hidden pb-4">
            {/* 1. GHOST / BACKGROUND TABLE */}
            <div className="w-full text-left border-collapse select-none pointer-events-auto">
                {/* Desktop Header */}
                {columns.length > 0 && (
                    <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3.5 bg-white border-b border-slate-100 text-xs font-medium text-slate-400">
                        {columns.map((col, idx) => (
                            <div 
                                key={idx} 
                                className={`${col.span || 'col-span-2'} ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'}`}
                            >
                                {col.label}
                            </div>
                        ))}
                    </div>
                )}

                {/* Desktop Rows */}
                {rows.length > 0 && renderRow && (
                    <div className="divide-y divide-slate-100/60">
                        {rows.map((row, index) => (
                            <React.Fragment key={row.id || index}>
                                {renderRow(row, index)}
                            </React.Fragment>
                        ))}
                    </div>
                )}

                {/* Mobile Cards */}
                {rows.length > 0 && renderMobileRow && (
                    <div className="md:hidden p-3 space-y-2.5">
                        {rows.slice(0, 2).map((row, index) => (
                            <React.Fragment key={row.id || index}>
                                {renderMobileRow(row, index)}
                            </React.Fragment>
                        ))}
                    </div>
                )}
            </div>

            {/* 2. FOREGROUND FLOATING CARD OVERLAY */}
            {showOverlayCard && (
                <div className="relative z-20 -mt-28 sm:-mt-36 md:-mt-44 px-3 sm:px-6 flex justify-center items-center">
                    <div className={`w-full ${dropZone ? 'max-w-3xl p-5 sm:p-7' : 'max-w-lg p-6 sm:p-8 text-center'} bg-white rounded-3xl border border-slate-200/90 shadow-sm relative backdrop-blur-md`}>
                        {dropZone ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8 items-center text-left">
                                
                                {/* Left Text & Actions */}
                                <div className="flex flex-col justify-between space-y-3.5">
                                    <div>
                                        <h2 className="text-xl sm:text-2xl font-normal text-slate-900 tracking-tight">
                                            {title}
                                        </h2>
                                        <p className="text-xs sm:text-[13px] text-slate-500 leading-relaxed mt-2 font-normal">
                                            {description}
                                        </p>
                                    </div>

                                    <div className="space-y-2.5 pt-1">
                                        {primaryAction && (
                                            primaryAction.isLocked ? (
                                                <PlanGate
                                                    featureKey="voucher-creation"
                                                    featureName="Voucher Creation"
                                                    tooltipText={primaryAction.tooltipText || "Subscription required to create vouchers. Upgrade to unlock."}
                                                    showLockIcon={false}
                                                    wrapperClassName="w-full"
                                                >
                                                    {({ isLocked }) => (
                                                        <button
                                                            type="button"
                                                            onClick={primaryAction.onClick}
                                                            className="w-full bg-[#067978] text-white py-2.5 sm:py-3 px-4 rounded-2xl font-normal text-xs sm:text-sm flex items-center justify-center gap-2 shadow-sm shadow-[#067978]/20 transition-all active:scale-[0.98] cursor-pointer"
                                                        >
                                                            {primaryAction.icon}
                                                            <span>{primaryAction.label}</span>
                                                            <LockIcon size={14} className="ml-1 text-white/90 shrink-0" />
                                                        </button>
                                                    )}
                                                </PlanGate>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={primaryAction.onClick}
                                                    className="w-full bg-[#067978] hover:bg-[#056463] text-white py-2.5 sm:py-3 px-4 rounded-2xl font-normal text-xs sm:text-sm flex items-center justify-center gap-2 shadow-sm shadow-[#067978]/20 transition-all active:scale-[0.98] cursor-pointer"
                                                >
                                                    {primaryAction.icon}
                                                    <span>{primaryAction.label}</span>
                                                    {primaryAction.rightIcon}
                                                </button>
                                            )
                                        )}

                                        {secondaryAction && (
                                            <button
                                                type="button"
                                                onClick={secondaryAction.onClick}
                                                className="w-full bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 hover:border-slate-300 py-2.5 sm:py-3 px-4 rounded-2xl font-normal text-xs sm:text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98] cursor-pointer shadow-2xs"
                                            >
                                                {secondaryAction.icon}
                                                <span>{secondaryAction.label}</span>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Right Drop Zone */}
                                <div className="md:border-l md:border-slate-100 md:pl-8 flex flex-col items-center justify-center">
                                    {dropZone.isLocked ? (
                                        <PlanGate
                                            featureKey="voucher-creation"
                                            featureName="Voucher Creation"
                                            tooltipText={dropZone.tooltipText || "Subscription required to create vouchers. Upgrade to unlock."}
                                            showLockIcon={false}
                                            wrapperClassName="w-full"
                                        >
                                            {() => (
                                                <div
                                                    onClick={dropZone.onDropZoneClick || (() => {})}
                                                    className="w-full rounded-2xl border-2 border-dashed p-5 sm:p-6 flex flex-col items-center justify-center text-center transition-all duration-200 cursor-pointer border-teal-200/90 bg-[#f2faf8]"
                                                >
                                                    <div className="w-11 h-11 rounded-xl border border-teal-300/80 bg-white flex items-center justify-center text-[#067978] shadow-2xs mb-2.5">
                                                        <LockIcon size={18} className="stroke-[2.5]" />
                                                    </div>

                                                    <h3 className="text-sm sm:text-base font-normal text-slate-800 leading-snug">
                                                        {dropZone.title}
                                                    </h3>

                                                    {dropZone.subtitle && (
                                                        <p className="text-[11px] sm:text-xs text-slate-400 mt-1 font-normal">
                                                            {dropZone.subtitle}
                                                        </p>
                                                    )}

                                                    {dropZone.badgeText && (
                                                        <span className="mt-3 inline-flex items-center gap-1 bg-[#067978] text-white text-[10px] font-normal tracking-widest uppercase px-3 py-0.5 rounded-full shadow-2xs">
                                                            <span>{dropZone.badgeText}</span>
                                                            <LockIcon size={10} className="text-white/90" />
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </PlanGate>
                                    ) : (
                                        <div
                                            onDragOver={handleDragOver}
                                            onDragLeave={handleDragLeave}
                                            onDrop={handleDrop}
                                            onClick={dropZone.onDropZoneClick || (() => fileInputRef.current?.click())}
                                            className={`w-full rounded-2xl border-2 border-dashed p-5 sm:p-6 flex flex-col items-center justify-center text-center transition-all duration-200 cursor-pointer ${
                                                isDragging
                                                    ? 'border-[#067978] bg-[#067978]/10 scale-[1.02]'
                                                    : 'border-teal-200/90 hover:border-[#067978] bg-[#f2faf8] hover:bg-[#eaf7f5]'
                                            }`}
                                        >
                                            <div className="w-11 h-11 rounded-xl border border-teal-300/80 bg-white flex items-center justify-center text-[#067978] shadow-2xs mb-2.5">
                                                {dropZone.icon || <ArrowUp size={18} className="stroke-[2.5]" />}
                                            </div>

                                            <h3 className="text-sm sm:text-base font-normal text-slate-800 leading-snug">
                                                {dropZone.title}
                                            </h3>

                                            {dropZone.subtitle && (
                                                <p className="text-[11px] sm:text-xs text-slate-400 mt-1 font-normal">
                                                    {dropZone.subtitle}
                                                </p>
                                            )}

                                            {dropZone.badgeText && (
                                                <span className="mt-3 inline-block bg-[#067978] text-white text-[10px] font-normal tracking-widest uppercase px-3 py-0.5 rounded-full shadow-2xs">
                                                    {dropZone.badgeText}
                                                </span>
                                            )}

                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                onChange={handleFileInputChange}
                                                accept={dropZone.accept || '.pdf,image/png,image/jpeg,image/jpg'}
                                                className="hidden"
                                            />
                                        </div>
                                    )}
                                </div>

                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center space-y-3.5 max-w-md mx-auto">
                                <h2 className="text-xl sm:text-2xl font-normal text-slate-900 tracking-tight">
                                    {title}
                                </h2>
                                {description && (
                                    <p className="text-xs sm:text-[13px] text-slate-500 leading-relaxed font-normal">
                                        {description}
                                    </p>
                                )}

                                {/* Centered Action Buttons (if any) */}
                                {(primaryAction || secondaryAction) && (
                                    <div className="flex flex-col sm:flex-row items-center justify-center gap-2.5 w-full pt-1">
                                        {primaryAction && (
                                            <button
                                                type="button"
                                                onClick={primaryAction.onClick}
                                                className="w-full sm:w-auto min-w-[180px] bg-[#067978] hover:bg-[#056463] text-white py-2.5 sm:py-3 px-5 rounded-2xl font-normal text-xs sm:text-sm flex items-center justify-center gap-2 shadow-sm shadow-[#067978]/20 transition-all active:scale-[0.98] cursor-pointer"
                                            >
                                                {primaryAction.icon}
                                                <span>{primaryAction.label}</span>
                                                {primaryAction.rightIcon}
                                            </button>
                                        )}
                                        {secondaryAction && (
                                            <button
                                                type="button"
                                                onClick={secondaryAction.onClick}
                                                className="w-full sm:w-auto bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 hover:border-slate-300 py-2.5 sm:py-3 px-5 rounded-2xl font-normal text-xs sm:text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98] cursor-pointer shadow-2xs"
                                            >
                                                {secondaryAction.icon}
                                                <span>{secondaryAction.label}</span>
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Optional Modals / Portals */}
            {children}
        </div>
    );
};

export default EmptyStateTable;
